import React, { useState } from 'react';
import { useDownloads } from '../../contexts/DownloadsContext';
import fileService from '../../services/fileService';
import './DownloadsPopup.css';

const DownloadsPopup = () => {
  const { downloads, isPopupOpen, closePopup, removeDownload, updateDownload, clearAllDownloads } = useDownloads();
  const [showClearWarning, setShowClearWarning] = useState(false);

  const handleDownload = async (download) => {
    console.log('📥 handleDownload called for:', download.fileName, 'blob exists:', !!download.blob);
    
    if (download.blob) {
      // Use stored blob if available
      try {
        const url = window.URL.createObjectURL(download.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = download.fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
        console.log('✅ Download triggered from stored blob');
      } catch (error) {
        console.error('❌ Error downloading from blob:', error);
        // Fall through to re-download from server
      }
    } else if (download.fileIds && download.fileIds.length > 0) {
      // Re-download from server if blob is not available (skip tracking to avoid duplicate entries)
      console.log('📥 Blob not available, re-downloading from server...', download.fileIds);
      try {
        await fileService.downloadFilesAsZip(
          download.fileIds,
          download.fileName,
          null,
          null,
          true,
          download.namePrefix ? { namePrefix: download.namePrefix } : {}
        );
        console.log('✅ Re-download completed and download should have been triggered');
      } catch (error) {
        console.error('❌ Error re-downloading:', error);
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        alert(`Failed to download: ${error.message}`);
      }
    } else {
      console.warn('⚠️ No blob or fileIds available for download');
      alert('Unable to download: file information is missing');
    }
  };

  const handleCancelDownload = (download) => {
    // Cancel the axios request
    if (download.cancelTokenSource) {
      download.cancelTokenSource.cancel('Download cancelled by user');
      console.log('✅ ZIP download cancelled by user');
    }
    
    // Remove the download from the list
    removeDownload(download.id);
  };

  const handleClearAll = () => {
    // Check if there are any active downloads
    const activeDownloads = downloads.filter(
      d => d.status === 'creating' || d.status === 'downloading'
    );
    
    if (activeDownloads.length > 0) {
      // Show custom warning popup
      setShowClearWarning(true);
    } else {
      // No active downloads, clear immediately
      clearAllDownloads();
    }
  };

  const handleConfirmClear = () => {
    setShowClearWarning(false);
    clearAllDownloads();
  };

  const handleCancelClear = () => {
    setShowClearWarning(false);
  };

  if (!isPopupOpen) return null;

  return (
    <div className="downloads-popup">
      <div className="downloads-popup-header">
        <div className="downloads-popup-header-left">
          <h3>Downloads</h3>

        </div>
        <button className="downloads-popup-close" onClick={closePopup} title="Close">
          <i className="fas fa-times"></i>
        </button>
      </div>
      {downloads.length > 0 && (
            <button 
              className="downloads-popup-clear-all" 
              onClick={handleClearAll} 
              title="Clear All"
            >
              Clear all
            </button>
          )}
      <div className="downloads-popup-content">
        {showClearWarning && (
          <div className="downloads-clear-warning-overlay">
            <div className="downloads-clear-warning-popup">
              <div className="downloads-clear-warning-icon">
                {/* <i className="fas fa-exclamation-triangle"></i> */}
              </div>
              <h4>This action will cancel all active downloads!</h4>
              <div className="downloads-clear-warning-buttons">
                <button 
                  className="downloads-clear-warning-cancel"
                  onClick={handleCancelClear}
                >
                  Go Back
                </button>
                <button 
                  className="downloads-clear-warning-confirm"
                  onClick={handleConfirmClear}
                >
                  Aprrove
                </button>
              </div>
            </div>
          </div>
        )}
        {downloads.length === 0 ? (
          <div className="downloads-empty">
            <i className="fas fa-download"></i>
            <p>No downloads</p>
          </div>
        ) : (
          <div className="downloads-list">
            {downloads.map((download) => (
              <div key={download.id} className="download-item">
                <div className="download-item-info">
                  <div className="download-item-icon">
                    {download.status === 'creating' || download.status === 'downloading' ? (
                      <div className="download-loading-circle"></div>
                    ) : download.status === 'completed' ? (
                      <i className="fas fa-check-circle"></i>
                    ) : (
                      <i className="fas fa-exclamation-circle"></i>
                    )}
                  </div>
                  <div className="download-item-details">
                    <div className="download-item-name">{download.fileName}</div>
                    <div className="download-item-status">
                      {download.status === 'creating' && 'Preparing download...'}
                      {download.status === 'downloading' && `Downloading... ${Math.round(download.progress)}%`}
                      {download.status === 'completed' && 'Ready'}
                      {download.status === 'error' && 'Error'}
                    </div>
                  </div>
                </div>
                
                <div className="download-item-actions">
                  {download.status === 'completed' && (
                    <button
                      className="download-item-download-btn"
                      onClick={() => handleDownload(download)}
                      title="Download again"
                    >
                      <i className="fas fa-download"></i>
                    </button>
                  )}
                  {(download.status === 'creating' || download.status === 'downloading') ? (
                    <button
                      className="download-item-cancel-btn"
                      onClick={() => handleCancelDownload(download)}
                      title="Cancel ZIP"
                    >
                      <i className="fas fa-times"></i>
                      <span className="cancel-btn-text">Cancel ZIP</span>
                    </button>
                  ) : (
                    <button
                      className="download-item-remove-btn"
                      onClick={() => removeDownload(download.id)}
                      title="Remove"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadsPopup;

